import {
  AfterContentInit,
  ContentChildren,
  Directive,
  EventEmitter,
  OnDestroy,
  Output,
  QueryList,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { mergeAll, of, Subscription } from 'rxjs';

import { SortKeyDirective } from './sort-key.directive';

export interface SortableProp {
  key: string;
  direction: 'asc' | 'desc' | null;
}

@Directive({
  selector: '[sortNav]',
})
export class SortNavDirective implements AfterContentInit, OnDestroy {
  @ContentChildren(SortKeyDirective) private sortHeadings: QueryList<SortKeyDirective>;
  @Output() private sortNav: EventEmitter<string> = new EventEmitter();
  private clicks$ = new Subscription();
  private sorting: SortableProp[] = [];

  constructor(
    private readonly activeRoute: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngAfterContentInit(): void {
    this.watchChanges();
    this.checkSortInUrl();
  }

  ngOnDestroy(): void {
    this.clicks$.unsubscribe();
  }

  private getSortQueryStringForAPI(): string {
    return this.sorting.map((el) => `${el.key}:${el.direction}`).join(',');
  }

  private getSortQueryObject(): { [k: string]: string } {
    const applicableSorts = this.sorting.map((el) => [el.key, el.direction]);
    return applicableSorts.reduce(
      (acc, curr) => ({
        ...acc,
        [curr[0]]: curr[1],
      }),
      {},
    );
  }

  private watchChanges(): void {
    const clicks = this.sortHeadings.map((h) => h.keyClicked);
    this.clicks$.add(
      of(...clicks)
        .pipe(mergeAll())
        .subscribe((el) => {
          this.updateSortEntries(el);
          this.updateCurrentUrl();
          this.sorting = this.sorting.filter((i) => i.direction !== null);
          this.sortNav.emit(this.getSortQueryStringForAPI());
          this.updateSortView();
        }),
    );
  }

  private updateSortEntries(entry: SortableProp): void {
    const entryIdx = this.sorting.findIndex((el) => el.key === entry.key);
    if (entryIdx === -1) {
      this.sorting = [...this.sorting, entry];
    } else {
      this.sorting.splice(entryIdx, 1, entry);
    }
  }

  private updateSortView(): void {
    this.sortHeadings.forEach((h) => {
      if (h.sortKey) {
        const sortKeyOrder = this.sorting.findIndex((el) => el.key === h.sortKey);
        h.sortOrder = sortKeyOrder + 1;
        h.buildSortNav();
      }
    });
  }

  private updateCurrentUrl(): void {
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: this.getSortQueryObject(),
      queryParamsHandling: 'merge',
    });
  }

  private checkSortInUrl(): void {
    const paramsRelatedToSort = Object.entries(this.activeRoute.snapshot.queryParams).filter(
      ([, dir]) => dir === 'asc' || dir === 'desc',
    );
    if (paramsRelatedToSort.length > 0) {
      const mappedParams: SortableProp[] = paramsRelatedToSort.map(([key, value]) => ({ key, direction: value }));
      this.sorting = [...mappedParams];
      this.sortNav.emit(this.getSortQueryStringForAPI());
      this.updateSortHeadings();
    }
  }

  private updateSortHeadings(): void {
    this.sorting.forEach((el) => {
      const heading = this.sortHeadings.find((h) => h.sortKey === el.key);
      if (heading) {
        heading.sortDirection = el.direction;
      }
    });
    this.updateSortView();
  }
}
